import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import Swal from 'sweetalert2';
import { Package, Users, Trash2, Edit3, Save, X, ArrowLeft } from 'lucide-react';
import API_BASE_URL from "../config";

export default function LicenseSelectionPage({ setNewUserData, setSelectedUser }) {
    // State Management
    const [filteredServices, setFilteredServices] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [clients, setClients] = useState([]);
    const [usersByClient, setUsersByClient] = useState({});
    const [viewingServiceDetails, setViewingServiceDetails] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [viewingClient, setViewingClient] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [togglingUserId, setTogglingUserId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch License Cards from API
    useEffect(() => {
        const fetchLicenseCards = async () => {
            setLoadingUsers(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/LicenseKey`, {
                    headers: { 'accept': 'text/plain' }
                });

                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const apiLicenses = await response.json();
                const apiKeys = apiLicenses
                    .filter(item => item.isActive)
                    .map(item => item.licenseKeyValue);

                const ref = collection(db, "services");
                const snapshot = await getDocs(ref);

                const filtered = snapshot.docs
                    .filter(doc => apiKeys.includes(doc.id))
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        hasLicense: true,
                    }));

                setFilteredServices(filtered);
            } catch (err) {
                console.error("Error loading license cards:", err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error Loading Licenses',
                    text: 'Failed to fetch license data. Please check if the server is running.',
                });
                setFilteredServices([]);
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchLicenseCards();
    }, []);

    // Utility Functions
    const formatDate = (timestamp) => {
        if (!timestamp?.seconds) return "N/A";
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    };

    const parseDateToTimestamp = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return { seconds: Math.floor(date.getTime() / 1000) };
    };

    // Client Management
    const handleDeleteClient = async (clientId) => {
        try {
            const confirm = await Swal.fire({
                title: 'Are you sure?',
                text: "This will permanently delete the client and all its users!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Yes, delete it!',
            });

            if (!confirm.isConfirmed) return;

            const clientRef = doc(db, "services", selectedService.id, "clients", clientId);
            await deleteDoc(clientRef);

            setClients(prev => prev.filter(c => c.id !== clientId));
            setUsersByClient(prev => {
                const updated = { ...prev };
                delete updated[clientId];
                return updated;
            });

            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Client has been deleted.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error deleting client:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to delete client.',
            });
        }
    };

    // Service Click Handler
    const handleServiceClick = async (service) => {
        setSelectedService(service);
        setViewingServiceDetails(true);
        setClients([]);
        setUsersByClient({});
        setLoadingUsers(true);

        try {
            const clientsRef = collection(db, "services", service.id, "clients");
            const clientSnap = await getDocs(clientsRef);
            const clientList = clientSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setClients(clientList);

            const usersMap = {};
            for (const client of clientList) {
                const usersRef = collection(db, "services", service.id, "clients", client.id, "users");
                const userSnap = await getDocs(usersRef);
                usersMap[client.id] = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }

            setUsersByClient(usersMap);
        } catch (err) {
            console.error("Error loading clients and users:", err);
        } finally {
            setLoadingUsers(false);
        }
    };

    // User Row Click Handler
    const handleRowClick = async (user) => {
        if (user.isTaken) return;

        try {
            const userRef = doc(db, "services", selectedService.id, "clients", selectedClientId, "users", user.id);
            await updateDoc(userRef, { isTaken: true });

            setUsersByClient(prev => ({
                ...prev,
                [selectedClientId]: prev[selectedClientId].map(u =>
                    u.id === user.id ? { ...u, isTaken: true } : u
                ),
            }));

            localStorage.setItem("selectedLicenseUser", JSON.stringify(user));
            localStorage.setItem("selectedLicenseKey", user.licenseKey || '');

            setNewUserData(prev => ({
                ...prev,
                licensekey: user.licenseKey,
                userCode: user.userCode || '',
                subscriptionStart: user.subscriptionStart,
                subscriptionEnd: user.subscriptionEnd,
            }));

            setSelectedUser(user);

            await Swal.fire({
                icon: 'success',
                title: 'License Assigned!',
                text: `User ${user.userCode || user.id} marked as taken.`,
            });
        } catch (error) {
            console.error("Failed to mark license as taken:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to assign the license.',
            });
        }
    };

    // Toggle isTaken Status
    const handleToggleIsTaken = async (user) => {
        const newIsTaken = !user.isTaken;
        setTogglingUserId(user.id);

        try {
            const userRef = doc(db, "services", selectedService.id, "clients", selectedClientId, "users", user.id);
            await updateDoc(userRef, { isTaken: newIsTaken });

            setUsersByClient(prev => ({
                ...prev,
                [selectedClientId]: prev[selectedClientId].map(u =>
                    u.id === user.id ? { ...u, isTaken: newIsTaken } : u
                ),
            }));

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `License status updated to ${newIsTaken ? "Taken" : "Available"}.`,
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error toggling isTaken:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to update status.',
            });
        } finally {
            setTogglingUserId(null);
        }
    };

    // Date Save Handler
    const handleDateSave = async (user, field, value) => {
        const timestamp = parseDateToTimestamp(value);

        if (!timestamp && value !== '') {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Date',
                text: 'Please enter a valid date.',
            });
            return;
        }

        try {
            const userRef = doc(db, "services", selectedService.id, "clients", selectedClientId, "users", user.id);
            const updateData = value === '' ? { [field]: null } : { [field]: timestamp };

            await updateDoc(userRef, updateData);

            setUsersByClient(prev => ({
                ...prev,
                [selectedClientId]: prev[selectedClientId].map(u =>
                    u.id === user.id ? { ...u, [field]: timestamp } : u
                ),
            }));

            Swal.fire({
                icon: 'success',
                title: 'Date Updated',
                text: `${field === 'subscriptionStart' ? 'Start' : 'End'} date updated successfully.`,
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error updating date:", error);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: 'Could not update the date.',
            });
        }
    };

    // Filter and Sort Users
    const getFilteredAndSortedUsers = () => {
        if (!usersByClient[selectedClientId]) return [];
        
        let filtered = usersByClient[selectedClientId].filter(user => {
            const searchLower = searchTerm.toLowerCase();
            return (
                user.licenseKey?.toLowerCase().includes(searchLower) ||
                user.userCode?.toLowerCase().includes(searchLower) ||
                user.id?.toLowerCase().includes(searchLower)
            );
        });

        filtered.sort((a, b) => {
            let aVal, bVal;
            
            if (sortBy === 'createdAt') {
                aVal = a.createdAt?.seconds || 0;
                bVal = b.createdAt?.seconds || 0;
            } else if (sortBy === 'userCode') {
                aVal = a.userCode || '';
                bVal = b.userCode || '';
            } else if (sortBy === 'isTaken') {
                aVal = a.isTaken ? 1 : 0;
                bVal = b.isTaken ? 1 : 0;
            } else if (sortBy === 'subscriptionStart') {
                aVal = a.subscriptionStart?.seconds || 0;
                bVal = b.subscriptionStart?.seconds || 0;
            } else if (sortBy === 'subscriptionEnd') {
                aVal = a.subscriptionEnd?.seconds || 0;
                bVal = b.subscriptionEnd?.seconds || 0;
            }

            return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });

        return filtered;
    };

    const filteredUsers = getFilteredAndSortedUsers();
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="min-h-screen   p-6 md:p-10">
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .card-hover {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .card-hover:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                }
            `}</style>

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 mb-8 shadow-2xl">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shadow-lg">
                            <Package size={32} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900">License Management</h2>
                            <p className="text-gray-600 mt-1">Manage your license keys and user assignments</p>
                        </div>
                    </div>
                </div>

                {loadingUsers ? (
                    <div className="bg-white/95 rounded-3xl p-20 text-center shadow-2xl">
                        <div className="w-16 h-16 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6"></div>
                        <p className="text-gray-600 text-lg font-medium">Loading license keys...</p>
                    </div>
                ) : !viewingServiceDetails ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredServices.length === 0 ? (
                            <div className="col-span-full bg-white/95 rounded-3xl p-20 text-center shadow-2xl">
                                <Package size={56} className="text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg font-medium">No license keys found</p>
                            </div>
                        ) : (
                            filteredServices.map(service => (
                                <div
                                    key={service.id}
                                    onClick={() => handleServiceClick(service)}
                                    className="card-hover bg-white/95 backdrop-blur-xl rounded-2xl p-7 cursor-pointer shadow-xl border-2 border-transparent hover:border-blue-500"
                                >
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center mb-5 shadow-lg">
                                        <Package size={28} className="text-white" />
                                    </div>
                                    <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                        LICENSE KEY
                                    </h5>
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 break-all">
                                        {service.id}
                                    </h3>
                                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                                        <p className="text-sm text-gray-700 font-medium mb-2">
                                            <strong>Name:</strong> {service.name || "N/A"}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Created: {service.createdAt
                                                ? new Date(service.createdAt.seconds * 1000).toLocaleDateString('en-US', { 
                                                    year: 'numeric', month: 'short', day: 'numeric' 
                                                })
                                                : "Unknown"}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <div>
                        <button
                            onClick={() => {
                                setViewingServiceDetails(false);
                                setSelectedClientId(null);
                                setViewingClient(false);
                                setEditingUserId(null);
                            }}
                            className="bg-white/95 px-6 py-3 rounded-xl font-semibold text-blue-600 hover:shadow-lg transition-all mb-6 inline-flex items-center gap-2"
                        >
                            <ArrowLeft size={18} />
                            Back to Services
                        </button>

                        {!viewingClient ? (
                            <div className="bg-white/95 rounded-3xl p-8 shadow-2xl">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center">
                                        <Users size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <h4 className="text-2xl font-bold text-gray-900">Clients</h4>
                                        <p className="text-sm text-gray-600">Service: {selectedService?.id}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {clients.map(client => (
                                        <div
                                            key={client.id}
                                            onClick={() => {
                                                setSelectedClientId(client.id);
                                                setViewingClient(true);
                                            }}
                                            className="card-hover bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-7 cursor-pointer relative overflow-hidden shadow-xl"
                                        >
                                            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full"></div>
                                            
                                            <h5 className="text-xl font-bold text-white mb-3 relative z-10">
                                                {client.name || "Client Name N/A"}
                                            </h5>
                                            <p className="text-sm text-white/90 font-mono mb-5 relative z-10">
                                                ID: {client.id}
                                            </p>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClient(client.id);
                                                }}
                                                className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 relative z-10 shadow-lg transition-all"
                                            >
                                                <Trash2 size={16} />
                                                Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white/95 rounded-3xl p-8 shadow-2xl">
                                <button
                                    onClick={() => {
                                        setViewingClient(false);
                                        setSelectedClientId(null);
                                        setEditingUserId(null);
                                    }}
                                    className="bg-white border-2 border-gray-300 px-6 py-3 rounded-xl font-semibold text-blue-600 hover:shadow-lg transition-all mb-6 inline-flex items-center gap-2"
                                >
                                    <ArrowLeft size={18} />
                                    Back to Clients
                                </button>

                                <h5 className="text-xl font-bold text-gray-900 mb-6">
                                    Users of Client: <span className="text-blue-600">{selectedClientId}</span>
                                </h5>

                                {/* Search and Filter */}
                                <div className="flex flex-wrap gap-4 mb-6">
                                    <input
                                        type="text"
                                        placeholder="🔍 Search by License Key, User Code, or ID..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="flex-1 min-w-[250px] px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-blue-500 outline-none text-sm"
                                    />
                                    
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-3 rounded-xl border-2 border-gray-300 text-sm font-medium cursor-pointer outline-none bg-white"
                                    >
                                        <option value="createdAt">Sort by: Created Date</option>
                                        <option value="userCode">Sort by: User Code</option>
                                        <option value="isTaken">Sort by: Status</option>
                                        <option value="subscriptionStart">Sort by: Start Date</option>
                                        <option value="subscriptionEnd">Sort by: End Date</option>
                                    </select>

                                    <button
                                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                        className="px-5 py-3 rounded-xl border-2 border-gray-300 bg-white text-sm font-semibold hover:border-blue-500 transition-all"
                                    >
                                        {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                                    </button>
                                </div>

                                <div className="bg-gray-100 px-4 py-3 rounded-xl mb-4 flex justify-between items-center">
                                    <span className="text-sm text-gray-600 font-medium">
                                        Showing {paginatedUsers.length} of {filteredUsers.length} users
                                    </span>
                                    <span className="text-sm text-gray-600">
                                        Page {currentPage} of {totalPages || 1}
                                    </span>
                                </div>

                                {/* Users Table */}
                                <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-lg">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-blue-600 to-blue-500">
                                                <th className="px-4 py-4 text-left text-white font-semibold text-sm">License Key</th>
                                                <th className="px-4 py-4 text-left text-white font-semibold text-sm">User Code</th>
                                                <th className="px-4 py-4 text-left text-white font-semibold text-sm">Created At</th>
                                                <th className="px-4 py-4 text-center text-white font-semibold text-sm">Status</th>
                                                <th className="px-4 py-4 text-left text-white font-semibold text-sm">Start Date</th>
                                                <th className="px-4 py-4 text-left text-white font-semibold text-sm">End Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white">
                                            {paginatedUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                                                        No users found matching your search.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedUsers.map(user => {
                                                    const createdDate = user.createdAt
                                                        ? new Date(user.createdAt.seconds * 1000).toLocaleString()
                                                        : "Unknown";

                                                    return (
                                                        <tr
                                                            key={user.id}
                                                            onClick={() => handleRowClick(user)}
                                                            className="border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer"
                                                        >
                                                            <td className="px-4 py-4 text-sm text-gray-700 font-mono">{user.licenseKey}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-700 font-medium">{user.userCode || "N/A"}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-600">{createdDate}</td>
                                                            <td className="px-4 py-4 text-center">
                                                                <div className="flex justify-center items-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={user.isTaken}
                                                                        disabled={togglingUserId === user.id}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            handleToggleIsTaken(user);
                                                                        }}
                                                                        className="w-5 h-5 cursor-pointer accent-blue-600"
                                                                    />
                                                                    {togglingUserId === user.id && (
                                                                        <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <input
                                                                    type="date"
                                                                    value={user.subscriptionStart?.seconds
                                                                        ? new Date(user.subscriptionStart.seconds * 1000).toISOString().slice(0, 10)
                                                                        : ''}
                                                                    onChange={(e) => e.stopPropagation()}
                                                                    onBlur={(e) => handleDateSave(user, 'subscriptionStart', e.target.value)}
                                                                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm max-w-[150px]"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <input
                                                                    type="date"
                                                                    value={user.subscriptionEnd?.seconds
                                                                        ? new Date(user.subscriptionEnd.seconds * 1000).toISOString().slice(0, 10)
                                                                        : ''}
                                                                    onChange={(e) => e.stopPropagation()}
                                                                    onBlur={(e) => handleDateSave(user, 'subscriptionEnd', e.target.value)}
                                                                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm max-w-[150px]"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="flex justify-center items-center gap-2 p-6 border-t border-gray-200">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold ${
                                                    currentPage === 1
                                                        ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                                                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-500'
                                                }`}
                                            >
                                                Previous
                                            </button>

                                            {[...Array(totalPages)].map((_, i) => {
                                                const page = i + 1;
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => setCurrentPage(page)}
                                                            className={`min-w-[40px] px-3 py-2 rounded-lg border-2 text-sm font-semibold ${
                                                                currentPage === page
                                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-500'
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                                    return <span key={page} className="text-gray-400">...</span>;
                                                }
                                                return null;
                                            })}

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold ${
                                                    currentPage === totalPages
                                                        ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                                                        : 'bg-white border-gray-300 text-gray-700 hover :border-blue-500'
                                                }`}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}